import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import moment from "moment";
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils";

export const eventList = createAsyncThunk('event/list', async ({ pageable: data, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `event`,
            params: {
                'pageSize': data?.pageSize,
                'pageNo': data?.currentPage,
                'search': data?.search,
                'premise_id': data?.premise_id,
                'rsvp_required': data?.rsvp_required,
                'start_date': data?.start_date,
                'end_date': data?.end_date,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const eventDetails = createAsyncThunk('event/details', async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `event/${id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addEvent = createAsyncThunk('event/add', async (data) => {

    const bodyFormData = new FormData()

    bodyFormData.append("premise_id", data?.values?.premise_id ?? '')
    bodyFormData.append("event_title", data?.values?.title ?? '')
    bodyFormData.append("event_description", data?.values?.description ?? '')
    bodyFormData.append("event_start_date", data?.values?.start_date && moment(data?.values?.start_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
    bodyFormData.append("event_end_date", data?.values?.end_date && moment(data?.values?.end_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
    bodyFormData.append("event_start_time", data?.values?.start_time ?? '')
    bodyFormData.append("event_end_time", data?.values?.end_time ?? '')
    bodyFormData.append("event_venue", data?.values?.venue ?? '')
    bodyFormData.append("event_capacity", data?.values?.max_capacity ?? '')
    bodyFormData.append("rsvp_allowed", data?.values?.rsvp_required)
    bodyFormData.append("rsvp_deadline", data?.values?.rsvp_deadline ? moment(data?.values?.rsvp_deadline, 'DD/MM/YYYY').format('YYYY-MM-DD') : '')

    if (data?.values?.banner) {
        bodyFormData.append("event_banner", data?.values?.banner)
    }

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'event',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "event_banner", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updateEvent = createAsyncThunk('event/update', async (data) => {

    const bodyFormData = new FormData()

    bodyFormData.append("event_id", data?.values?.id)
    bodyFormData.append("premise_id", data?.values?.premise_id ?? '')
    bodyFormData.append("event_title", data?.values?.title ?? '')
    bodyFormData.append("event_description", data?.values?.description ?? '')
    bodyFormData.append("event_start_date", data?.values?.start_date && moment(data?.values?.start_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
    bodyFormData.append("event_end_date", data?.values?.end_date && moment(data?.values?.end_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
    bodyFormData.append("event_start_time", data?.values?.start_time ?? '')
    bodyFormData.append("event_end_time", data?.values?.end_time ?? '')
    bodyFormData.append("event_venue", data?.values?.venue ?? '')
    bodyFormData.append("event_capacity", data?.values?.max_capacity ?? '')
    bodyFormData.append("rsvp_allowed", data?.values?.rsvp_required)
    bodyFormData.append("rsvp_deadline", data?.values?.rsvp_deadline ? moment(data?.values?.rsvp_deadline, 'DD/MM/YYYY').format('YYYY-MM-DD') : '')

    if (data?.values?.banner) {
        bodyFormData.append("event_banner", data?.values?.banner)
    }

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'update_event',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "event_banner", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const delEvent = createAsyncThunk('event/delete', async (data) => {
    try {
        const res = await axios({
            method: 'DELETE',
            url: getConfig.API_URL + `event/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const submitEventRsvp = createAsyncThunk('event/rsvp', async ({ rsvp_id, premise_id, event_id, rsvp_status }) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'rsvp',
            data: {
                rsvp_id,
                premise_id,
                event_id,
                rsvp_status,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const event = createSlice({
    name: 'event',
    initialState: {
        list: [],
        details: null,
        loading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(eventList.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.list = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        });

        builder.addCase(eventDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        });
    }
})

export default event.reducer;